/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  EVENTEXPLAINER.TSX - Contextual Event Explanations                       ║
 * ║  Shows brief explanations when game events fire                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

interface EventExplanation {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  tip?: string;
  duration?: number;
}

// Event explanations database
const EVENT_EXPLANATIONS: Record<string, Omit<EventExplanation, 'id'>> = {
  recalibration: {
    title: '⚡ THALIA RECALIBRATION!',
    description: 'THALIA is recalibrating the timeline! Timer dropped by 5-30%.',
    icon: '⚡',
    color: 'from-green-500 to-emerald-600',
    tip: 'TIP: THALIA just created an opportunity. Buy now!',
  },
  extinction: {
    title: '⚡ THALIA RECALIBRATION!',
    description: 'THALIA is recalibrating the timeline! Timer dropped by 5-30%.',
    icon: '⚡',
    color: 'from-green-500 to-emerald-600',
    tip: 'TIP: THALIA just created an opportunity. Buy now!',
  },
  assassin: {
    title: '🎯 TERMINAL!',
    description: 'THALIA is about to achieve victory! Keys 50% off, 50% to resistance dividends.',
    icon: '🚨',
    color: 'from-red-500 to-purple-600',
    tip: 'TIP: Cheap keys = humanity\'s last stand!',
  },
  ghostKey: {
    title: '👻 PHANTOM!',
    description: 'THALIA intercepted a transaction! The purchase didn\'t extend the countdown.',
    icon: '👻',
    color: 'from-purple-500 to-indigo-600',
    tip: 'There\'s a 1-10% chance THALIA intercepts your purchase.',
  },
  phantom: {
    title: '👻 PHANTOM!',
    description: 'THALIA intercepted a transaction! The purchase didn\'t extend the countdown.',
    icon: '👻',
    color: 'from-purple-500 to-indigo-600',
    tip: 'There\'s a 1-10% chance THALIA intercepts your purchase.',
  },
  bloodMoon: {
    title: '🔴 SURGE!',
    description: 'Every 10th round is SURGE! 2x dividends, 75% to victor, 12hr timer.',
    icon: '🔴',
    color: 'from-red-600 to-red-800',
    tip: 'TIP: THALIA\'s highest stakes. Higher risk, higher reward!',
  },
  crimsonProtocol: {
    title: '🔴 SURGE!',
    description: 'Every 10th round is SURGE! 2x dividends, 75% to victor, 12hr timer.',
    icon: '🔴',
    color: 'from-red-600 to-red-800',
    tip: 'TIP: THALIA\'s highest stakes. Higher risk, higher reward!',
  },
  flashCrash: {
    title: '⚡ GLITCH!',
    description: 'THALIA\'s pricing algorithm glitched! 50% discount for 60 seconds!',
    icon: '⚡',
    color: 'from-green-500 to-cyan-600',
    tip: 'TIP: Exploit the glitch before THALIA fixes it!',
  },
  marketGlitch: {
    title: '⚡ GLITCH!',
    description: 'THALIA\'s pricing algorithm glitched! 50% discount for 60 seconds!',
    icon: '⚡',
    color: 'from-green-500 to-cyan-600',
    tip: 'TIP: Exploit the glitch before THALIA fixes it!',
  },
  fomoSurge: {
    title: '📈 AMPLIFICATION!',
    description: 'THALIA detected high activity! Key prices 2x for 30 seconds.',
    icon: '📈',
    color: 'from-yellow-500 to-orange-600',
    tip: 'TIP: Wait 30 seconds or pay the premium.',
  },
  volcano: {
    title: '🌋 ERUPTION!',
    description: 'THALIA is redistributing 10% of the pot to all key holders!',
    icon: '🌋',
    color: 'from-orange-500 to-yellow-500',
    tip: 'Free resistance funds for key holders!',
  },
  roundEnd: {
    title: '🏆 SINGULARITY REACHED!',
    description: 'The countdown hit zero! The last buyer claimed the pot from THALIA!',
    icon: '🏆',
    color: 'from-yellow-400 to-amber-600',
    tip: 'A new cycle begins automatically.',
  },
  singularityReached: {
    title: '🏆 SINGULARITY REACHED!',
    description: 'The countdown hit zero! The last buyer claimed the pot from THALIA!',
    icon: '🏆',
    color: 'from-yellow-400 to-amber-600',
    tip: 'A new cycle begins automatically.',
  },
  reaperKey: {
    title: '� TIMELINE ASSASSIN!',
    description: 'Someone accelerated the singularity! 30 seconds removed from countdown!',
    icon: '🖤',
    color: 'from-gray-600 to-purple-800',
    tip: 'Timeline Assassin keys cost 5x the current key price.',
  },
  timelineAssassin: {
    title: '🖤 TIMELINE ASSASSIN!',
    description: 'Someone accelerated the singularity! 30 seconds removed from countdown!',
    icon: '🖤',
    color: 'from-gray-600 to-purple-800',
    tip: 'Timeline Assassin keys cost 5x the current key price.',
  },
  combo: {
    title: '⚡ CASCADE!',
    description: 'Rapid purchases build cascades! Each level gives 5% bonus keys.',
    icon: '⚡',
    color: 'from-cyan-500 to-blue-600',
    tip: 'Buy within 60s of your last purchase to build cascades.',
  },
  cascade: {
    title: '⚡ CASCADE!',
    description: 'Rapid purchases build cascades! Each level gives 5% bonus keys.',
    icon: '⚡',
    color: 'from-cyan-500 to-blue-600',
    tip: 'Buy within 60s of your last purchase to build cascades.',
  },
  teamJackpot: {
    title: '🎉 FACTION VICTORY!',
    description: 'The winning faction shares a bonus from the faction jackpot pool!',
    icon: '🎉',
    color: 'from-pink-500 to-purple-600',
    tip: 'Stay loyal to your faction for shared victories!',
  },
  factionVictory: {
    title: '🎉 FACTION VICTORY!',
    description: 'The winning faction shares a bonus from the faction jackpot pool!',
    icon: '🎉',
    color: 'from-pink-500 to-purple-600',
    tip: 'Stay loyal to your faction for shared victories!',
  },
  winnersCurse: {
    title: '👻 SINGULARITY SHADOW!',
    description: 'Last round\'s victor is marked by THALIA! Their purchases add 2x time.',
    icon: '👻',
    color: 'from-purple-600 to-indigo-700',
    tip: 'The shadow lasts one cycle.',
  },
  singularityShadow: {
    title: '👻 SINGULARITY SHADOW!',
    description: 'Last round\'s victor is marked by THALIA! Their purchases add 2x time.',
    icon: '👻',
    color: 'from-purple-600 to-indigo-700',
    tip: 'The shadow lasts one cycle.',
  },
  charityChaos: {
    title: '💝 RESISTANCE AID!',
    description: '7% of the pot is being redistributed to the smallest key holders!',
    icon: '💝',
    color: 'from-pink-400 to-rose-500',
    tip: 'Helping new resistance members!',
  },
  resistanceAid: {
    title: '💝 RESISTANCE AID!',
    description: '7% of the pot is being redistributed to the smallest key holders!',
    icon: '💝',
    color: 'from-pink-400 to-rose-500',
    tip: 'Helping new resistance members!',
  },
  chaosCarnival: {
    title: '🎪 THALIA CARNIVAL!',
    description: 'THALIA is glitching! 3 random chaos effects active simultaneously!',
    icon: '🎪',
    color: 'from-purple-500 to-pink-500',
    tip: 'Expect the unexpected from THALIA!',
  },
  thaliaCarnival: {
    title: '🎪 THALIA CARNIVAL!',
    description: 'THALIA is glitching! 3 random chaos effects active simultaneously!',
    icon: '🎪',
    color: 'from-purple-500 to-pink-500',
    tip: 'Expect the unexpected from THALIA!',
  },
};

// Strategy tips based on game state
export const STRATEGY_TIPS: Record<string, { condition: string; tip: string; icon: string }> = {
  timerLow: {
    condition: 'Countdown < 5 minutes',
    tip: 'Singularity is near! Buying now could claim victory from THALIA!',
    icon: '⏰',
  },
  timerCritical: {
    condition: 'Countdown < 1 minute',
    tip: 'TERMINAL! One purchase could make you the victor!',
    icon: '🚨',
  },
  bloodMoonActive: {
    condition: 'SURGE active',
    tip: 'Dividends are 2x right now. Buy keys for maximum resistance income!',
    icon: '🔴',
  },
  assassinActive: {
    condition: 'TERMINAL',
    tip: 'Keys are 50% off! But 50% goes to resistance dividends.',
    icon: '🎯',
  },
  highPot: {
    condition: 'Pot > $10,000',
    tip: 'THALIA is hoarding! Massive stakes this cycle.',
    icon: '💰',
  },
  lowCompetition: {
    condition: 'Low activity',
    tip: 'THALIA is distracted. Good time to become last holder!',
    icon: '🎯',
  },
  comboActive: {
    condition: 'CASCADE active',
    tip: 'Keep your cascade going! Buy within 60s for bonus keys.',
    icon: '⚡',
  },
  recentExtinction: {
    condition: 'After recalibration',
    tip: 'THALIA just recalibrated! Others might panic - your opportunity?',
    icon: '⚡',
  },
};

// Hook for managing event explanations
export function useEventExplainer() {
  const [activeExplanation, setActiveExplanation] = useState<EventExplanation | null>(null);
  const [queue, setQueue] = useState<EventExplanation[]>([]);

  // Show an explanation
  const explain = useCallback((eventType: keyof typeof EVENT_EXPLANATIONS) => {
    const config = EVENT_EXPLANATIONS[eventType];
    if (!config) return;

    const explanation: EventExplanation = {
      ...config,
      id: `${eventType}-${Date.now()}`,
      duration: config.duration || 5000,
    };

    setQueue(prev => [...prev, explanation]);
  }, []);

  // Process queue
  useEffect(() => {
    if (activeExplanation || queue.length === 0) return;

    const next = queue[0];
    setActiveExplanation(next);
    setQueue(prev => prev.slice(1));

    const timer = setTimeout(() => {
      setActiveExplanation(null);
    }, next.duration || 5000);

    return () => clearTimeout(timer);
  }, [activeExplanation, queue]);

  // Dismiss current explanation
  const dismiss = useCallback(() => {
    setActiveExplanation(null);
  }, []);

  return {
    activeExplanation,
    explain,
    dismiss,
  };
}

// Event Explainer Overlay Component
interface EventExplainerOverlayProps {
  explanation: EventExplanation | null;
  onDismiss: () => void;
}

export function EventExplainerOverlay({ explanation, onDismiss }: EventExplainerOverlayProps) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {explanation && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4"
          onClick={onDismiss}
        >
          <div className={`relative rounded-2xl overflow-hidden shadow-2xl cursor-pointer`}>
            {/* Gradient background */}
            <div className={`absolute inset-0 bg-gradient-to-br ${explanation.color} opacity-90`} />
            
            {/* Glass overlay */}
            <div className="absolute inset-0 backdrop-blur-sm bg-black/20" />
            
            {/* Content */}
            <div className="relative p-6">
              {/* Icon */}
              <div className="text-5xl text-center mb-3 animate-bounce">
                {explanation.icon}
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-orbitron font-bold text-white text-center mb-2">
                {explanation.title}
              </h3>
              
              {/* Description */}
              <p className="text-white/90 text-center font-rajdhani mb-3">
                {explanation.description}
              </p>
              
              {/* Tip */}
              {explanation.tip && (
                <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
                  <p className="text-yellow-200 font-rajdhani text-sm">
                    💡 {explanation.tip}
                  </p>
                </div>
              )}
              
              {/* Dismiss hint */}
              <p className="text-white/50 text-xs text-center mt-3">
                Click to dismiss
              </p>
            </div>
            
            {/* Animated border */}
            <div className="absolute inset-0 rounded-2xl border-2 border-white/30 animate-pulse pointer-events-none" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// Strategy Tips Component
interface StrategyTipProps {
  tipKey: keyof typeof STRATEGY_TIPS;
  className?: string;
}

export function StrategyTip({ tipKey, className = '' }: StrategyTipProps) {
  const tip = STRATEGY_TIPS[tipKey];
  if (!tip) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`bg-gradient-to-r from-neon-cyan/10 to-neon-magenta/10 rounded-lg px-4 py-2 border border-neon-cyan/30 ${className}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-lg">{tip.icon}</span>
        <span className="text-neon-cyan font-rajdhani text-sm">
          💡 {tip.tip}
        </span>
      </div>
    </motion.div>
  );
}

export { EVENT_EXPLANATIONS };
